/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import Layout from "~/components/Layout";
import Button from "~/components/Button";
import Input from "~/components/Input";
import { cpfMask, birthDateMask } from "~/utils/mask";
import useApi, { ApiMethod } from "~/hooks/useApi";
import Endpoints from "~/services/endpoints";
import ModalOneButton from "~/components/Modals/OneButtonPopup";

import themes from "~/themes";
import * as S from "./styles";
import { Content, Title, Subtitle } from "../../styles";
import ReCAPTCHA from "react-google-recaptcha";

/**
 * Renderiza a tela pública de consulta de fatura por CPF e data de nascimento.
 *
 * O componente coleta os dados informados pelo usuário, realiza a chamada para
 * obtenção da última fatura, controla o estado do reCAPTCHA e redireciona para
 * a tela de carregamento quando a consulta é concluída com sucesso.
 *
 * @returns {JSX.Element} Estrutura visual da tela de consulta de fatura.
 */
const inputCPF = (props) => {
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalText, setModalText] = useState("");
  const history = useHistory();

  const PostLastInvoiceByCpfAndBirthDate = useApi(
    Endpoints.relatorio.getLastInvoiceByCpfAndBirthDate,
    ApiMethod.POST,
  );

  /**
   * Verifica se os campos obrigatórios foram preenchidos completamente para
   * habilitar o botão de consulta, incluindo a validação do reCAPTCHA.
   *
   * @returns {boolean} Retorna `true` quando o botão deve permanecer desabilitado.
   */
  const handleDisabledButton = () =>
    cpf.length !== 14 || birthDate.length !== 10 || !recaptchaToken;

  /**
   * Busca a última fatura do cliente a partir do CPF e da data de nascimento.
   *
   * Quando a API retorna sucesso, os dados recebidos são complementados com os
   * valores informados no formulário e enviados para a próxima rota. O token do
   * reCAPTCHA é recuperado do estado local e enviado junto à requisição.
   *
   * @returns {Promise<void>} Promessa resolvida após a conclusão da operação.
   */
  const handleGetLastInvoice = async () => {
    if (cpf.length !== 14 || birthDate.length !== 10) return;
    if (!recaptchaToken) {
      alert("Por favor, complete o reCAPTCHA para continuar.");
      return;
    }

    try {
      const response = await PostLastInvoiceByCpfAndBirthDate.callApi({
        cpf: cpf.replace(/\D/g, ""),
        dataNascimento: birthDate.split("/").reverse().join("-"),
        recaptchaToken,
      });

      if (response.status === 200) {
        const dataWithExtra = {
          ...response.data,
          dataNascimento: birthDate,
          cpf: cpf.replace(/\D/g, ""),
        };

        history.push("/Fatura-Loading", {
          data: dataWithExtra,
        });
      } else {
        setModalText(
          response?.data?.mensagem || "Não foi possível consultar sua fatura.",
        );
        setModalIsOpen(true);
      }
    } catch (err) {
      console.error("Erro ao buscar fatura:", err);
      setModalText(
        err?.response?.data?.mensagem ||
          err?.message ||
          "Não foi possível consultar sua fatura no momento.",
      );
      setModalIsOpen(true);
    }
  };

  /**
   * Atualiza o estado local com o token gerado após a validação do reCAPTCHA.
   *
   * @param {string|null} value - Token retornado pelo Google reCAPTCHA.
   * @returns {void}
   */
  const onChangeReCAPTCHA = (value) => {
    setRecaptchaToken(value);
    console.log("Captcha resolvido com o token:", value);
  };

  return (
    <Layout auth={false}>
      <ModalOneButton
        ishtml
        text={modalText}
        buttonText="Ok"
        icon={themes.components.icons.tick}
        visibility={modalIsOpen}
        setVisibility={(bool) => setModalIsOpen(bool)}
      />
      <Content>
        <img src={themes.components.images.logoTeuCard} alt="Logo TeuCard" />
        <p>
          <strong>Bem-vindo</strong> à Fatura TeuCard
        </p>

        <S.ContentInput>
          <Title>Consultar fatura</Title>
          <Subtitle>Informe teus dados para acessar</Subtitle>

          <Input
            type="text"
            inputmode="numeric"
            label="CPF"
            placeholder="000.000.000-00"
            maxLength={14}
            value={cpf}
            leftIcon={themes.components.icons.user}
            onChange={(e) => setCpf(cpfMask(e.target.value))}
          />

          <Input
            type="text"
            inputmode="numeric"
            label="Data de nascimento"
            placeholder="00/00/0000"
            maxLength={10}
            value={birthDate}
            leftIcon={themes.components.icons.calendarGrey}
            onChange={(e) => setBirthDate(birthDateMask(e.target.value))}
          />
        </S.ContentInput>

        <S.ContentButton>
          <Button
            disabled={handleDisabledButton()}
            onClick={handleGetLastInvoice}
          >
            Consultar
          </Button>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_V2_SECRET}
            onChange={onChangeReCAPTCHA}
            onExpired={() => setRecaptchaToken(null)}
            onErrored={() => setRecaptchaToken(null)}
            theme="light" // Opcional: 'dark' ou 'light'
            style={{ marginTop: "20px" }}
          />
        </S.ContentButton>
      </Content>
    </Layout>
  );
};

export default inputCPF;